import React from "react";
import {Box, CircularProgress, Stack, Typography} from "@mui/material";
import {isSoilMoistureAlert, soilMoistureAlertColor} from "../../utils/insightsUtils";
import {StatusIndicator} from "../base/StatusIndicator";
import {useTranslation} from "react-i18next";
import {HelpPopover} from "../base/HelpPopover";

export interface Props {
    soilMoisturePercentage: number | undefined;
    threshold?: number;
    vegetationCover: number | undefined;
    isLoading: boolean;
}

export const FieldSoilMoistureCard: React.FC<Props> = ({
    soilMoisturePercentage,
    isLoading,
    threshold,
    vegetationCover,
}) => {
    const {t} = useTranslation();
    const thresholdValue = threshold ?? 0.1;
    const lineHeight = 140 - 140 * 0.85 * thresholdValue;
    const textHeight = 140 - 140 * 0.85 + lineHeight / 2 - 10;
    const soilMoistureValue = soilMoisturePercentage && soilMoisturePercentage > 100 ? 100 : soilMoisturePercentage;
    return (
        <Stack direction="column" alignItems="center" gap="1.25rem" position="relative">
            <Stack direction="row" alignItems="center" gap="0.75rem">
                <Typography textTransform="uppercase" fontSize={16} fontWeight={500} color="#545A6E">
                    {t("insights.field_soil_moisture.soil_moisture")}
                </Typography>
                <StatusIndicator
                    status={
                        soilMoistureValue !== undefined &&
                        isSoilMoistureAlert(soilMoistureValue, thresholdValue, vegetationCover)
                            ? "moistureAlert"
                            : "normal"
                    }
                />
                <Box sx={{position: "absolute", right: "-1.5rem", top: "-1.5rem"}}>
                    <HelpPopover i18nKey="help.popover.fields.soil_moisture" placement="top" direction="right" />
                </Box>
            </Stack>
            <Stack direction="column" alignItems="center" gap="0.5rem" position="relative">
                <Typography textTransform="uppercase" fontSize={14} fontWeight={500} color="#737990">
                    {t("insights.field_soil_moisture.saturated_soil")}
                </Typography>
                <svg width="218" height="160" viewBox="0 0 191 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        {!isLoading && soilMoistureValue !== undefined && !Number.isNaN(soilMoistureValue) && (
                            <linearGradient id="soilMoistureGrad" x2="0%" y2="100%">
                                <stop
                                    offset={1 - (soilMoistureValue / 100) * (soilMoistureValue === 100 ? 1 : 0.85)}
                                    stopColor="transparent"
                                />
                                <stop
                                    offset={1 - (soilMoistureValue / 100) * (soilMoistureValue === 100 ? 1 : 0.85)}
                                    stopColor={soilMoistureAlertColor(
                                        soilMoistureValue,
                                        thresholdValue,
                                        vegetationCover,
                                    )}
                                />
                            </linearGradient>
                        )}
                    </defs>
                    <line strokeDasharray="5, 5" x1="0" y1="23" x2="191" y2="23" stroke="#8F94A8"></line>
                    <line
                        strokeDasharray="5, 5"
                        x1="0"
                        y1={lineHeight}
                        x2="191"
                        y2={lineHeight}
                        stroke="#8F94A8"
                    ></line>
                    <path
                        d="M101.856 42.4555L101.856 42.456C109.967 50.2875 115.483 60.2587 117.718 71.1046C119.954 81.9498 118.811 93.1929 114.431 103.415C110.051 113.637 102.627 122.386 93.0864 128.547C83.5455 134.708 72.3209 138 60.833 138C49.3451 138 38.1206 134.708 28.5797 128.547C19.0394 122.386 11.6151 113.637 7.23485 103.415C2.85499 93.1929 1.71213 81.9498 3.94762 71.1046C6.18321 60.2587 11.6994 50.2875 19.8098 42.456L19.8115 42.4543L60.7967 2.78229L101.856 42.4555Z"
                        stroke="#5161C1"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        transform="translate(35,0)"
                        fill="url(#soilMoistureGrad)"
                    />
                    <text
                        textAnchor="middle"
                        fill="#737990"
                        fontSize={13.3 * (1 - thresholdValue)}
                        fontWeight={500}
                        transform={`translate(170, ${textHeight}) rotate(90)`}
                    >
                        {t("insights.field_soil_moisture.healthy")}
                    </text>
                    {!isLoading && (
                        <text x="50%" y="90" textAnchor="middle" fill="#3A3F4F" fontSize={36} fontWeight={700}>
                            {soilMoisturePercentage !== undefined &&
                                !Number.isNaN(soilMoisturePercentage) &&
                                soilMoisturePercentage <= 100 &&
                                `${Number(soilMoisturePercentage).toFixed(0)}%`}
                            {soilMoisturePercentage !== undefined &&
                                !Number.isNaN(soilMoisturePercentage) &&
                                soilMoisturePercentage > 100 &&
                                `max`}
                        </text>
                    )}
                </svg>
                {isLoading && <CircularProgress sx={{position: "absolute", top: "6rem"}} />}
                <Typography textTransform="uppercase" fontSize={14} fontWeight={500} color="#737990">
                    {t("insights.field_soil_moisture.dry_soil")}
                </Typography>
            </Stack>
        </Stack>
    );
};
